import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'

const PAYMENTENUM: Record<string, string> = {
  'IS CREDIT': 'اعتباری',
  CASH: 'نقدی',
}

const PAYMENTBYENUM: Record<number, string> = {
  0: 'فرستنده',
  1: 'گیرنده',
}

// --- helpers ------------------------------------------------------
const isFormula = (val: any): val is { formula: string } =>
  val &&
  typeof val === 'object' &&
  'formula' in val &&
  typeof val.formula === 'string'

const getCellFormula = (cell: ExcelJS.Cell): string | null => {
  const v: any = cell.value
  if (isFormula(v)) return v.formula
  if (
    v &&
    typeof v === 'object' &&
    'sharedFormula' in v &&
    typeof v.sharedFormula === 'string'
  ) {
    return v.sharedFormula as string
  }
  return null
}

const adjustFormulaRow = (formula: string, fromRow: number, toRow: number) => {
  return formula.replace(/(\$?[A-Z]{1,3}\$?)(\d+)/g, (m, col, row) => {
    const r = parseInt(row, 10)
    if (r === fromRow) return `${col}${toRow}`
    return m
  })
}

const applyStyles = (from: ExcelJS.Cell, to: ExcelJS.Cell) => {
  to.fill = from.fill
  to.font = from.font
  to.border = from.border
  to.alignment = from.alignment
  to.numFmt = from.numFmt
}

// --- main ---------------------------------------------------------
export const handleDownload = async (
  calculateData: any,
  apiResponse: any[]
) => {
  const response = await fetch('/creditCalculate.xlsx')
  const arrayBuffer = await response.arrayBuffer()

  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(arrayBuffer)

  // ------------------ شیت "ریز" ------------------
  const worksheet = workbook.getWorksheet('ریز')
  if (!worksheet) throw new Error('Worksheet "ریز" not found')

  // ذخیره اطلاعات header
  const headerRow = worksheet.getRow(1)
  const headerData: Record<number, any> = {}
  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    headerData[colNumber] = {
      value: cell.value,
      formula: getCellFormula(cell),
      fill: cell.fill!,
      font: cell.font || {},
      border: cell.border || {},
      alignment: cell.alignment || {},
      numFmt: cell.numFmt || '',
    }
  })

  // ذخیره اطلاعات template row (ردیف 2)
  const templateRow = worksheet.getRow(2)
  const templateData: Record<number, any> = {}
  templateRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    templateData[colNumber] = {
      value: cell.value,
      formula: getCellFormula(cell),
      fill: cell.fill!,
      font: cell.font || {},
      border: cell.border || {},
      alignment: cell.alignment || {},
      numFmt: cell.numFmt || '',
    }
  })

  // پاک کردن داده‌های قدیمی (جز header و template)
  const lastRow = worksheet.rowCount
  for (let i = lastRow; i >= 3; i--) {
    worksheet.spliceRows(i, 1)
  }

  // ---------- پر کردن ردیف 2 با اولین داده ----------
  if (apiResponse.length > 0) {
    const firstItem = apiResponse[0]
    const row = worksheet.getRow(2)

    row.getCell(1).value = firstItem.cprNumber ?? null
    row.getCell(2).value = firstItem.sumExtraCost ?? 0
    row.getCell(4).value = firstItem.sumGheramat ?? 0
    row.getCell(6).value = firstItem.sumBasePrice ?? 0
    row.getCell(9).value = firstItem.sumOther ?? 0
    row.getCell(11).value = PAYMENTBYENUM[firstItem.postPayed] ?? 0
    row.getCell(12).value = firstItem.discount ?? 0
    row.getCell(13).value = firstItem.sums ?? 0
    row.getCell(14).value = firstItem.maliat ?? 0
    row.getCell(15).value = firstItem.sumMaliat ?? 0
    row.getCell(16).value = firstItem.paymentBy
      ? PAYMENTENUM[firstItem.paymentBy] ?? firstItem.paymentBy
      : null
    row.getCell(17).value = firstItem.senderName
    row.getCell(18).value = firstItem.receiverName
    row.getCell(19).value = firstItem.receiverCityName
    row.getCell(20).value = firstItem.persianCreateDate.split(" ")[0]
    // استایل + فرمول‌ها
    const currentRowNumber = row.number
    Object.keys(templateData).forEach((key) => {
      const col = Number(key)
      const tmpl = templateData[col]
      const targetCell = row.getCell(col)

      targetCell.fill = tmpl.fill
      targetCell.font = tmpl.font
      targetCell.border = tmpl.border
      targetCell.alignment = tmpl.alignment
      targetCell.numFmt = tmpl.numFmt

      if (tmpl.formula && !targetCell.value) {
        const adjusted = adjustFormulaRow(tmpl.formula, 2, currentRowNumber)
        targetCell.value = { formula: adjusted }
      } else if (
        !targetCell.value &&
        tmpl.value !== undefined &&
        tmpl.value !== null
      ) {
        targetCell.value = tmpl.value
      }
    })
  }

  // ---------- پر کردن بقیه داده‌ها از ردیف 3 به بعد ----------
  apiResponse.slice(1).forEach((item) => {
    const newRow = worksheet.addRow([])

    newRow.getCell(1).value = item.cprNumber ?? null
    newRow.getCell(2).value = item.sumExtraCost ?? 0
    newRow.getCell(4).value = item.sumGheramat ?? 0
    newRow.getCell(6).value = item.sumBasePrice ?? 0
    newRow.getCell(9).value = item.sumOther ?? 0
    newRow.getCell(11).value = PAYMENTBYENUM[item.postPayed] ?? 0
    newRow.getCell(12).value = item.discount ?? 0
    newRow.getCell(13).value = item.sums ?? 0
    newRow.getCell(14).value = item.maliat ?? 0
    newRow.getCell(15).value = item.sumMaliat ?? 0
    newRow.getCell(16).value = item.paymentBy
      ? PAYMENTENUM[item.paymentBy] ?? item.paymentBy
      : null
    newRow.getCell(17).value = item.senderName
    newRow.getCell(18).value = item.receiverName
    newRow.getCell(19).value = item.receiverCityName
    newRow.getCell(20).value = item.persianCreateDate.split(" ")[0]
    const currentRowNumber = newRow.number
    Object.keys(templateData).forEach((key) => {
      const col = Number(key)
      const tmpl = templateData[col]
      const targetCell = newRow.getCell(col)

      targetCell.fill = tmpl.fill
      targetCell.font = tmpl.font
      targetCell.border = tmpl.border
      targetCell.alignment = tmpl.alignment
      targetCell.numFmt = tmpl.numFmt

      if (tmpl.formula && !targetCell.value) {
        const adjusted = adjustFormulaRow(tmpl.formula, 2, currentRowNumber)
        targetCell.value = { formula: adjusted }
      } else if (
        !targetCell.value &&
        tmpl.value !== undefined &&
        tmpl.value !== null
      ) {
        targetCell.value = tmpl.value
      }
    })
  })

  // بازگردانی header
  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const src = headerData[colNumber]
    if (!src) return
    applyStyles(cell, cell)
    if (src.formula) {
      cell.value = { formula: src.formula }
    } else {
      cell.value = src.value
    }
  })

  // ------------------ شیت "محاسبات" ------------------
  const calcWorksheet = workbook.getWorksheet('محاسبات')
  if (calcWorksheet) {
    const updateCellValue = (cellAddress: string, value: any) => {
      const cell = calcWorksheet.getCell(cellAddress)
      const existingFormula = getCellFormula(cell)

      if (existingFormula) {
        cell.value = { formula: existingFormula }
      } else {
        cell.value = value
      }
    }

    updateCellValue('B2', calculateData.allCreditCount || 0)
    updateCellValue('B3', calculateData.allCashCount || 0)
    updateCellValue('B4', calculateData.allPostPayedCount || 0)

    updateCellValue('C2', calculateData.asli_Credit || 0)
    updateCellValue('C3', calculateData.asli_Cash || 0)
    updateCellValue('C4', calculateData.asli_PostPayed || 0)

    updateCellValue('D2', calculateData.asli_Credit_ExtraCost || 0)
    updateCellValue('D3', calculateData.asli_Cash_ExtraCost || 0)
    updateCellValue('D4', calculateData.asli_PostPayed_ExtraCost || 0)

    updateCellValue('E2', calculateData.asli_Credit_Ghetamat || 0)
    updateCellValue('E3', calculateData.asli_Cash_Ghetamat || 0)
    updateCellValue('E4', calculateData.asli_PostPayed_Gheramat || 0)

    updateCellValue('F2', calculateData.asli_Credit_OtherService || 0)
    updateCellValue('F3', calculateData.asli_Cash_OtherService || 0)
    updateCellValue('F4', calculateData.asli_PostPayed_OtherService || 0)

    updateCellValue('H2', calculateData.percentage || 0)
    updateCellValue('H3', calculateData.percentage || 0)
    updateCellValue('H4', calculateData.percentage || 0)
   
    updateCellValue('M2', calculateData.totalPayment || 0)
    updateCellValue('N2', calculateData.totalDepositToHub || 0)
  }

  workbook.calcProperties.fullCalcOnLoad = true

  const buffer = await workbook.xlsx.writeBuffer()
  saveAs(new Blob([buffer]), 'filled-data.xlsx')
}
